import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, Chip, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, LinearProgress, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  groupContactRepairWorkbookRows, normalizeContactRepairWorkbook, planBatchContactRepair,
} from "./contactRepairBatchPlan";

const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const MAX_GROUPS = 200;
const MAX_WORKBOOK_ROWS = 12000;
const MAX_FILE_SIZE = 8 * 1024 * 1024;
const MATCH_BATCH_SIZE = 50;
const MAX_SELECTED_ACTIONS = 150;
const getError = (error) => error?.response?.data?.error ||
  error?.response?.data?.message || error?.message || "Contact repair request failed.";
const chunk = (items, size) =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, index * size + size));
const selectionKey = (row, action) => `${row.customerId}:${action.id}`;
const headers = () => ({ headers: { accessToken: localStorage.getItem("accessToken") } });

const formatStatus = (status) => ({
  ready: "Ready", empty: "No safe changes", ambiguous: "Customer name duplicated",
  "not-found": "Customer not found", error: "Scan failed",
  pending: "Pending", stopped: "Not scanned",
}[status] || status);
const statusColor = (status) => ({
  ready: "success", empty: "default", ambiguous: "warning",
  "not-found": "warning", error: "error",
}[status] || "default");

const csvSafe = (value) => {
  const text = String(value ?? "").replace(/[\r\n\0]/g, " ");
  const safe = /^[=+@-]/.test(text.trimStart()) ? "'" + text : text;
  return '"' + safe.replace(/"/g, '""') + '"';
};
const downloadOutcomes = (outcomes) => {
  const rows = [
    ["Customer ID", "Customer", "Action", "Contact / email", "Result", "Details", "Audit ID"],
    ...outcomes.map((item) => [
      item.customerId, item.customerName, item.kind, item.label,
      item.status, item.message, item.auditId || "",
    ]),
  ];
  const csv = rows.map((row) => row.map(csvSafe).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "elite-core-contact-repair-results.csv";
  anchor.click();
  URL.revokeObjectURL(url);
};

export default function ContactRepairBatchDialog({ open, onClose, instanceUniqueId }) {
  const [fileName, setFileName] = useState("");
  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [stage, setStage] = useState("upload");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [outcomes, setOutcomes] = useState([]);
  const [busy, setBusy] = useState(false);
  const stopRef = useRef(false);
  const activeRef = useRef(0);
  const endpoint = `${SERVER}/customers/contact-repair`;

  useEffect(() => {
    // Invalidate old previews whenever the dialog opens/closes OR the
    // active instance changes. Never apply one tenant's workbook to another.
    stopRef.current = true;
    activeRef.current += 1;
    setFileName("");
    setGroups([]);
    setRows([]);
    setSelected([]);
    setStage("upload");
    setError("");
    setProgress({ done: 0, total: 0 });
    setConfirmText("");
    setOutcomes([]);
    setExpanded(null);
    setSearch("");
    setBusy(false);
    return undefined;
  }, [open, instanceUniqueId]);

  const pickWorkbook = async (file) => {
    if (!file) return;
    const run = activeRef.current;
    setError("");
    setRows([]);
    setSelected([]);
    setOutcomes([]);
    setConfirmText("");
    try {
      if (!/\.(xlsx|xls)$/i.test(file.name) || file.size > MAX_FILE_SIZE) {
        throw new Error("Choose an Excel .xlsx or .xls workbook under 8 MB.");
      }
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      let parsed = null;
      let format = "elite-core";
      for (const name of ["Your Clients", "ERP Import", ...workbook.SheetNames]) {
        const sheet = workbook.Sheets[name];
        if (!sheet) continue;
        for (const range of [1, 0]) {
          const candidate = XLSX.utils.sheet_to_json(sheet, { range, defval: "" });
          if (!candidate.length) continue;
          const columns = candidate[0];
          if (["Row Type *", "Client Name *", "First Name *", "Email *"]
            .every((header) => Object.prototype.hasOwnProperty.call(columns, header))) {
            parsed = candidate;
            format = "elite-core";
            break;
          }
          if (["Record Type", "Client Name", "First Name", "Last Name", "Email", "Import?"]
            .every((header) => Object.prototype.hasOwnProperty.call(columns, header))) {
            parsed = candidate;
            format = "erp-import";
            break;
          }
        }
        if (parsed) break;
      }
      if (!parsed) throw new Error("Choose an Elite Core client-import workbook or an ERP Import workbook with Record Type, Client Name, First Name, Last Name, Email and Import? columns.");
      if (parsed.length > MAX_WORKBOOK_ROWS) {
        throw new Error("The workbook has too many rows for a bounded Contact Repair analysis.");
      }
      const nextGroups = groupContactRepairWorkbookRows(normalizeContactRepairWorkbook(parsed, format));
      if (activeRef.current !== run) return;
      if (!nextGroups.length) throw new Error("No CONTACT rows with customer names were found.");
      if (nextGroups.length > MAX_GROUPS) {
        throw new Error(`This tool supports up to ${MAX_GROUPS} customers per workbook. Please split the sheet.`);
      }
      setFileName(file.name);
      setGroups(nextGroups);
      setStage("upload");
    } catch (err) {
      if (activeRef.current !== run) return;
      setGroups([]);
      setFileName("");
      setError(getError(err));
    }
  };

  const analyze = async () => {
    if (busy || !groups.length) return;
    stopRef.current = false;
    const run = ++activeRef.current;
    setBusy(true);
    setError("");
    setStage("analyzing");
    setSelected([]);
    setConfirmText("");
    setOutcomes([]);
    setExpanded(null);
    setProgress({ done: 0, total: groups.length });

    const work = groups.map((group) => ({ ...group, customerId: null, status: "pending",
      matches: [], preview: null, actions: [] }));
    setRows([...work]);
    let count = 0;
    try {
      for (const batch of chunk(groups, MATCH_BATCH_SIZE)) {
        if (stopRef.current || activeRef.current !== run) break;
        const { data } = await axios.post(
          `${endpoint}/batch/${instanceUniqueId}/match-customers`,
          { names: batch.map((group) => group.sourceName) }, headers());
        if (activeRef.current !== run || stopRef.current) break;
        for (const result of data.results || []) {
          const row = work.find((item) =>
            item.sourceName.toLowerCase() === String(result.sourceName || "").toLowerCase());
          if (!row) continue;
          row.status = result.status === "matched" ? "pending" : result.status;
          row.matches = result.matches || [];
          if (result.status === "matched") row.customerId = result.matches[0].id;
          else count += 1;
        }
      }
      if (activeRef.current !== run) return;
      setRows([...work]);
      // Scan only uniquely matched customers. Two workers cap simultaneous
      // historical document scans so batch uploads do not overwhelm MySQL.
      const indexes = work.map((row, index) => row.customerId ? index : -1)
        .filter((index) => index >= 0);
      let cursor = 0;
      await Promise.all(Array.from({ length: 2 }, async () => {
        while (cursor < indexes.length && !stopRef.current && activeRef.current === run) {
          const index = indexes[cursor++];
          const row = work[index];
          try {
            const { data } = await axios.get(
              `${endpoint}/${row.customerId}/preview`, headers());
            if (stopRef.current || activeRef.current !== run) break;
            row.preview = data;
            const plan = planBatchContactRepair(data, row.sourceRows);
            row.actions = plan.actions;
            row.reviewCount = plan.reviewCount;
            row.status = plan.actions.length ? "ready" : "empty";
          } catch (err) {
            row.status = "error";
            row.error = getError(err);
          }
          count += 1;
          setProgress({ done: count, total: groups.length });
          setRows([...work]);
        }
      }));
      if (activeRef.current === run) {
        work.forEach((row) => { if (row.status === "pending") row.status = "stopped"; });
        setRows([...work]);
        setProgress({ done: count, total: groups.length });
        setStage("review");
      }
    } catch (err) {
      if (activeRef.current === run) {
        setError(getError(err));
        setStage("review");
        work.forEach((row) => { if (row.status === "pending") row.status = "stopped"; });
        setRows([...work]);
      }
    } finally {
      if (activeRef.current === run) setBusy(false);
    }
  };

  const allEligible = rows.flatMap((row) =>
    (row.preview?.writesEnabled ? row.actions || [] : [])
      .map((action) => selectionKey(row, action)));
  const selectedValid = selected.filter((key) => allEligible.includes(key));
  const selectedCount = selectedValid.length;
  const visible = rows.filter((row) =>
    [row.sourceName, row.customerId, row.status]
      .join(" ").toLowerCase().includes(search.trim().toLowerCase()));
  const summary = {
    matched: rows.filter((row) => row.customerId).length,
    notFound: rows.filter((row) => row.status === "not-found").length,
    ambiguous: rows.filter((row) => row.status === "ambiguous").length,
    ready: allEligible.length,
    needsReview: rows.reduce((n, row) => n + (row.reviewCount || 0), 0),
  };

  const addOutcome = (item) => setOutcomes((previous) => [...previous, item]);

  const applyBatch = async () => {
    if (busy || !selectedCount || selectedCount > MAX_SELECTED_ACTIONS ||
        confirmText.trim() !== `REPAIR ${selectedCount}`) return;
    if (!window.confirm(
      `Apply ${selectedCount} approved contact changes across the selected customers?
Each write will recheck historical references and create an audit record.
Failures will be skipped; the batch is not one global transaction.`)) return;
    stopRef.current = false;
    const run = ++activeRef.current;
    setBusy(true);
    setError("");
    setStage("applying");
    setOutcomes([]);
    setProgress({ done: 0, total: selectedCount });
    let done = 0;
    let applied = 0;
    let auditRunId = null;
    const recorded = new Set();
    const batchOutcomes = [];
    const record = (row, action, status, message, auditId = null) => {
      if (activeRef.current !== run) return;
      const id = selectionKey(row, action);
      if (recorded.has(id)) return;
      recorded.add(id);
      done += 1;
      if (status === "applied") applied += 1;
      const item = {
        customerId: row.customerId, customerName: row.sourceName,
        kind: action.kind, label: action.label + " / " + action.email,
        status, message, auditId,
      };
      batchOutcomes.push(item);
      addOutcome(item);
      setProgress({ done, total: selectedCount });
    };

    try {
      // Create a persistent batch audit before performing any contact writes.
      // Refuse the batch if auditing cannot be established.
      const started = await axios.post(
        `${endpoint}/batch/${instanceUniqueId}/audit/start`,
        { sourceName: fileName }, headers());
      auditRunId = started.data.runId;
      for (const row of rows) {
        const approved = (row.actions || []).filter((action) =>
          selectedValid.includes(selectionKey(row, action)));
        if (!approved.length) continue;
        if (stopRef.current || activeRef.current !== run) break;
        let current;
        try {
          const { data } = await axios.get(
            `${endpoint}/${row.customerId}/preview`, headers());
          current = data;
        } catch (err) {
          approved.forEach((action) => record(row, action, "failed", getError(err)));
          continue;
        }
        if (!row.preview || current.fingerprint !== row.preview.fingerprint) {
          approved.forEach((action) => record(row, action, "skipped",
            "Customer contacts changed since analysis; analyze again."));
          continue;
        }

        const merges = approved.filter((action) => action.kind === "merge");
        const recoveries = approved.filter((action) => action.kind === "recover");
        for (const batch of chunk(merges, 50)) {
          if (stopRef.current || activeRef.current !== run) break;
          const latestPlan = planBatchContactRepair(current, row.sourceRows);
          const valid = batch.filter((action) => latestPlan.actions.some(
            (fresh) => fresh.id === action.id && fresh.email === action.email));
          batch.filter((action) => !valid.includes(action)).forEach((action) =>
            record(row, action, "skipped", "Pairing no longer eligible; refresh preview."));
          if (!valid.length) continue;
          try {
            const { data } = await axios.post(`${endpoint}/${row.customerId}/apply`, {
              fingerprint: current.fingerprint,
              keys: valid.map((action) => action.key),
              reviewedKeys: [],
              auditRunId,
            }, headers());
            valid.forEach((action) => record(row, action, "applied",
              "Contacts consolidated; historical aliases preserved.", data.auditId));
          } catch (err) {
            valid.forEach((action) => record(row, action, "failed", getError(err)));
          }
          // A successful merge changes the fingerprint. Always fetch the new
          // current state before attempting another operation on this customer.
          try {
            const fresh = await axios.get(`${endpoint}/${row.customerId}/preview`, headers());
            current = fresh.data;
          } catch (err) {
            current = null;
            setError("One or more customers could not be rechecked after a repair.");
            break;
          }
        }

        for (const action of recoveries) {
          if (stopRef.current || activeRef.current !== run) break;
          if (!current) {
            record(row, action, "skipped", "Could not refresh customer after prior repair.");
            continue;
          }
          const plan = planBatchContactRepair(current, row.sourceRows);
          if (!plan.actions.some((fresh) => fresh.id === action.id &&
            fresh.email === action.email)) {
            record(row, action, "skipped", "Source email recovery is no longer eligible.");
            continue;
          }
          try {
            const { data } = await axios.post(`${endpoint}/${row.customerId}/source-email`, {
              fingerprint: current.fingerprint,
              contactId: action.contactId,
              sourceFirstName: action.firstName,
              sourceLastName: action.lastName,
              sourceEmail: action.email,
              confirmedEmail: action.email,
              auditRunId,
            }, headers());
            record(row, action, "applied",
              "Missing email restored from the reviewed workbook.", data.auditId);
          } catch (err) {
            record(row, action, "failed", getError(err));
          }
          try {
            const fresh = await axios.get(`${endpoint}/${row.customerId}/preview`, headers());
            current = fresh.data;
          } catch (err) { current = null; }
        }
      }
    } catch (err) {
      if (activeRef.current === run) setError(getError(err));
    } finally {
      // Account for actions not reached when a batch is stopped before
      // finalizing the durable audit, so the detailed report remains complete.
      if (activeRef.current === run) {
        for (const row of rows) {
          (row.actions || []).filter((action) =>
            selectedValid.includes(selectionKey(row, action)) &&
            !recorded.has(selectionKey(row, action)))
            .forEach((action) => record(row, action, "skipped", "Not processed; no change was made."));
        }
      }
      if (auditRunId) {
        try {
          await axios.post(
            `${endpoint}/batch/${instanceUniqueId}/audit/${auditRunId}/finish`,
            { selectedCount, outcomes: batchOutcomes.filter((item) => item.status !== "applied") }, headers());
        } catch (auditError) {
          if (activeRef.current === run) setError("Repairs finished, but their batch audit could not be finalized. An administrator should review Import History.");
        }
      }
      if (activeRef.current === run) {
        // Even if the operator stops the batch or a request fails
        // unexpectedly, the CSV accounts for every selected operation.
        for (const row of rows) {
          (row.actions || []).filter((action) =>
            selectedValid.includes(selectionKey(row, action)) &&
            !recorded.has(selectionKey(row, action)))
            .forEach((action) => record(row, action, "skipped",
              "Not processed; no change was made."));
        }
        if (stopRef.current) {
          setError("Batch stopped. Previously completed repairs remain saved and audited.");
        }
        setStage("results");
        setSelected([]);
        setConfirmText("");
        setBusy(false);
      }
    }
    return applied;
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xl">
      <DialogTitle>Contact Repair — Workbook Batch</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 440 }}>
        <Stack spacing={2}>
          <Alert severity="info">
            Administrator-only. Upload the same customer import workbook, review matched
            customers and select supported repairs. No changes occur during upload or analysis.
            Existing project/document references and conflicting contacts remain blocked.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <Button variant="outlined" component="label" startIcon={<FileUploadOutlinedIcon />}
              disabled={busy}>
              {fileName ? "Replace workbook" : "Choose Excel workbook"}
              <input hidden type="file" accept=".xlsx,.xls"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) pickWorkbook(file);
                  event.target.value = "";
                }} />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `${fileName} · ${groups.length} customers with CONTACT rows` :
                "Supported: Elite Core Client Import or ERP Import (.xlsx, .xls); 8 MB maximum"}
            </Typography>
            {groups.length > 0 && stage !== "applying" && (
              <Button variant="contained" disabled={busy} onClick={analyze}>
                {rows.length ? "Re-analyze workbook" : "Analyze customers"}
              </Button>
            )}
          </Stack>

          {busy && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  {stage === "applying" ? "Applying selected customer repairs" :
                    "Matching customers and checking historical references"}
                </Typography>
                <Typography variant="body2">{progress.done} / {progress.total}</Typography>
              </Stack>
              <LinearProgress variant="determinate"
                value={progress.total ? (progress.done / progress.total) * 100 : 0} />
              <Button size="small" onClick={() => { stopRef.current = true; }}>
                Stop after current request
              </Button>
            </Box>
          )}
          {rows.length > 0 && (
            <Stack spacing={2}>
              <Divider />
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Chip label={`${rows.length} customers in workbook`} />
                <Chip label={`${summary.matched} matched`} color="primary" variant="outlined" />
                <Chip label={`${summary.ready} source-supported actions`} color="success" variant="outlined" />
                <Chip label={`${summary.needsReview} require manual review`} variant="outlined" />
                {summary.ambiguous > 0 && <Chip label={`${summary.ambiguous} duplicate customer names`} color="warning" />}
                {summary.notFound > 0 && <Chip label={`${summary.notFound} not found`} color="warning" />}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Only unique company-name matches are scanned. The batch offers strong,
                spreadsheet-corroborated contact merges and unique, unlinked missing-email
                recoveries. Ambiguous or blocked suggestions stay in the individual customer popup.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField size="small" label="Filter customers" value={search}
                  onChange={(event) => setSearch(event.target.value)} fullWidth />
                <Button disabled={busy || stage !== "review" || !allEligible.length}
                  onClick={() => {
                    setSelected(allEligible.slice(0, MAX_SELECTED_ACTIONS));
                    setConfirmText("");
                  }}>
                  Select eligible (up to {MAX_SELECTED_ACTIONS})
                </Button>
                <Button disabled={busy || stage !== "review" || !selected.length}
                  onClick={() => { setSelected([]); setConfirmText(""); }}>
                  Clear
                </Button>
              </Stack>
              <Box sx={{ overflowX: "auto", maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Import contacts</TableCell>
                      <TableCell>Safe actions</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.map((row) => (
                      <React.Fragment key={row.key}>
                        <TableRow hover>
                          <TableCell>
                            <Typography fontWeight={600} variant="body2">{row.sourceName}</Typography>
                            {row.customerId && <Typography variant="caption" color="text.secondary">
                              Core #{row.customerId}
                            </Typography>}
                          </TableCell>
                          <TableCell>{row.sourceRows.length}</TableCell>
                          <TableCell>
                            {row.preview?.writesEnabled ? row.actions.length :
                              row.customerId && row.preview ? "Read only" : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={formatStatus(row.status)}
                              color={statusColor(row.status)} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Button size="small" disabled={!row.preview && !row.error &&
                              !row.matches?.length}
                              onClick={() => setExpanded(expanded === row.key ? null : row.key)}>
                              {expanded === row.key ? "Hide" : "Review"}
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                            <Collapse in={expanded === row.key} unmountOnExit>
                              <Stack spacing={1.5} sx={{ p: 2, bgcolor: "action.hover" }}>
                                {row.error && <Alert severity="error">{row.error}</Alert>}
                                {row.matches?.length > 1 && (
                                  <Alert severity="warning">
                                    Multiple Core customers share this name. No batch changes are allowed.
                                    IDs: {row.matches.map((item) => item.id).join(", ")}
                                  </Alert>
                                )}
                                {!row.customerId && row.status === "not-found" && (
                                  <Alert severity="info">
                                    No exact, instance-scoped customer name match. Review manually in Sales → Customers.
                                  </Alert>
                                )}
                                {row.preview && (
                                  <Typography variant="caption" color="text.secondary">
                                    {row.preview.summary.total} saved contacts · {row.preview.summary.suggestions} possible pairings ·
                                    {" "}{row.preview.summary.blocked} blocked · {row.reviewCount || 0} need review.
                                    Only safely corroborated changes are offered here.
                                  </Typography>
                                )}
                                {row.actions.length === 0 && row.customerId && (
                                  <Typography variant="body2">
                                    No eligible source-backed changes. The individual customer popup
                                    may still have manual or linked contact suggestions.
                                  </Typography>
                                )}
                                {(row.preview?.proposals || []).filter((pair) =>
                                  !pair.safe || pair.confidence !== "strong").slice(0, 5).map((pair) => (
                                  <Typography key={pair.key} variant="caption" color="text.secondary">
                                    Requires individual review: {pair.match} — {pair.blockedReasons?.join("; ") ||
                                      "Identity confirmation required"}
                                  </Typography>
                                ))}
                                {row.actions.map((action) => {
                                  const key = selectionKey(row, action);
                                  return (
                                    <Box key={key} sx={{ border: "1px solid", borderColor: "divider",
                                      bgcolor: "background.paper", borderRadius: 1, p: 1.5 }}>
                                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Checkbox size="small" checked={selected.includes(key)}
                                          disabled={busy || stage !== "review" || !row.preview?.writesEnabled}
                                          onChange={(event) => {
                                            setSelected((previous) => event.target.checked ?
                                              [...previous, key] : previous.filter((item) => item !== key));
                                            setConfirmText("");
                                          }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="body2" fontWeight={600}>
                                            {action.kind === "merge" ? "Consolidate" : "Recover missing email"}
                                            {" · "}{action.label}
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: "block", overflowWrap: "anywhere" }}>
                                            <strong>Before:</strong> {action.before}
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: "block", overflowWrap: "anywhere" }}>
                                            <strong>After:</strong> {action.after}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                            {action.description}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {action.kind === "merge" ?
                                              `Survivor UUID: ${action.keepId} · Retained alias UUID: ${action.removeId}` :
                                              `Existing contact UUID retained: ${action.contactId}`}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          )}

          {stage === "review" && selectedCount > 0 && (
            <Stack spacing={1}>
              <Alert severity="warning">
                {selectedCount} explicitly selected changes. This is not an all-or-nothing
                transaction across customers. Each successful customer write is separately
                audited; failures remain unchanged and appear in the results.
                Source emails are approved by this batch confirmation rather than retyped individually.
              </Alert>
              <TextField size="small" label={`Type REPAIR ${selectedCount} to confirm`}
                value={confirmText} onChange={(event) => setConfirmText(event.target.value)}
                helperText="Review selected customer changes above before applying." />
              <Button variant="contained" color="primary" onClick={applyBatch}
                disabled={busy || selectedCount > MAX_SELECTED_ACTIONS ||
                  confirmText.trim() !== `REPAIR ${selectedCount}`}>
                Apply {selectedCount} selected contact repairs
              </Button>
              {selectedCount > MAX_SELECTED_ACTIONS && (
                <Alert severity="error">
                  Select at most {MAX_SELECTED_ACTIONS} actions per batch to keep the update bounded.
                </Alert>
              )}
            </Stack>
          )}

          {stage === "results" && (
            <Stack spacing={1.5}>
              <Divider />
              <Typography variant="h6">Batch results</Typography>
              <Stack direction="row" spacing={1}>
                <Chip label={`${outcomes.filter((o) => o.status === "applied").length} applied`}
                  color="success" />
                <Chip label={`${outcomes.filter((o) => o.status === "skipped").length} skipped`} />
                <Chip label={`${outcomes.filter((o) => o.status === "failed").length} failed`}
                  color="error" />
              </Stack>
              <Alert severity="info">
                Results are shown per action. Any applied changes are already saved and
                have Contact Repair audit records; re-analyze the workbook to inspect
                remaining work. Unprocessed actions were not changed.
              </Alert>
              <Button size="small" variant="outlined" sx={{ alignSelf: "flex-start" }}
                disabled={!outcomes.length} onClick={() => downloadOutcomes(outcomes)}>
                Download results CSV
              </Button>
              <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Customer</TableCell><TableCell>Change</TableCell>
                    <TableCell>Result</TableCell><TableCell>Details</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {outcomes.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.customerName} (#{item.customerId})</TableCell>
                        <TableCell>{item.label}</TableCell>
                        <TableCell>
                          <Chip size="small" label={item.status} color={
                            item.status === "applied" ? "success" :
                              item.status === "failed" ? "error" : "default"} />
                        </TableCell>
                        <TableCell>{item.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
